import axios from '../../../config/axios';
import AttachmentApiService from '../../../services/AttachmentApiService';
import {
  CreateFinancialEntryPayload,
  CreateFinancialRecurrencePayload,
  CreateSplitResponse,
  FinancialEntryDetail,
  FinancialEntryListFilters,
  FinancialEntryListResponse,
  FinancialRecurrence,
  MaterializeOccurrencePayload,
  UpdateFinancialEntryPayload,
  UpdateFinancialRecurrencePayload,
} from './types';
import { filtersToCustomRules } from './financialFilterConfig';

const API_BASE = `${process.env.REACT_APP_GATEWAY_BASE_URL}/financial/entry`;
const RECURRENCE_API_BASE = `${process.env.REACT_APP_GATEWAY_BASE_URL}/financial/recurrence`;

const buildListParams = (filters: FinancialEntryListFilters) => {
  const params: Record<string, string | number> = {
    viewBy: filters.viewBy ?? 'entry',
  };

  if (filters.page) params.page = filters.page;
  if (filters.limit) params.limit = filters.limit;
  if (filters.search) params.search = filters.search;
  if (filters.createdAtFrom) params.createdAtFrom = filters.createdAtFrom;
  if (filters.createdAtTo) params.createdAtTo = filters.createdAtTo;
  if (filters.occurrenceFrom) params.occurrenceFrom = filters.occurrenceFrom;
  if (filters.occurrenceTo) params.occurrenceTo = filters.occurrenceTo;
  if (filters.sortBy) params.sortBy = filters.sortBy;

  const customRules = filtersToCustomRules(filters.appliedFilters ?? []);
  if (customRules.length) {
    params.customFilters = JSON.stringify(customRules);
  }

  return params;
};

export interface PaymentMethodOption {
  id: number;
  name: string;
}

export const financialEntryApi = {
  getPaymentMethods: async (): Promise<PaymentMethodOption[]> => {
    const { data } = await axios.get<PaymentMethodOption[]>(`${API_BASE}/payment-methods`);
    return data;
  },

  list: async (filters: FinancialEntryListFilters = {}): Promise<FinancialEntryListResponse> => {
    const { data } = await axios.get<FinancialEntryListResponse>(API_BASE, {
      params: buildListParams(filters),
    });
    return data;
  },

  getById: async (id: number): Promise<FinancialEntryDetail> => {
    const { data } = await axios.get<FinancialEntryDetail>(`${API_BASE}/${id}`);
    return data;
  },

  create: async (payload: CreateFinancialEntryPayload): Promise<FinancialEntryDetail> => {
    const { data } = await axios.post<FinancialEntryDetail>(API_BASE, payload);
    return data;
  },

  createSplit: async (payload: CreateFinancialEntryPayload): Promise<CreateSplitResponse> => {
    const { data } = await axios.post<CreateSplitResponse>(`${API_BASE}/split`, payload);
    return data;
  },

  update: async (id: number, payload: UpdateFinancialEntryPayload): Promise<FinancialEntryDetail> => {
    const { data } = await axios.patch<FinancialEntryDetail>(`${API_BASE}/${id}`, payload);
    return data;
  },

  remove: async (id: number): Promise<void> => {
    await axios.delete(`${API_BASE}/${id}`);
  },

  uploadFiles: async (itemId: number, files: File[]) => {
    const uploadResults = await Promise.all(
      files.map((file) => AttachmentApiService.upload(file)),
    );
    const fileIds = uploadResults
      .map((res: any) => res.id || res.generalFile?.id)
      .filter((id: any): id is number => typeof id === 'number');

    const { data } = await axios.post(`${API_BASE}/item/${itemId}/files`, { fileIds });
    return data;
  },

  attachFiles: async (itemId: number, fileIds: number[]) => {
    const { data } = await axios.post(`${API_BASE}/item/${itemId}/files`, { fileIds });
    return data;
  },

  getFileUrl: async (
    itemId: number,
    fileId: number,
    forceDownload = false,
  ): Promise<string> => {
    const { data } = await axios.get<{ url: string }>(
      `${API_BASE}/item/${itemId}/files/${fileId}/download`,
      { params: { forceDownload: forceDownload ? 'true' : 'false' } },
    );
    return data.url;
  },

  viewFile: async (itemId: number, fileId: number): Promise<string> =>
    financialEntryApi.getFileUrl(itemId, fileId, false),

  downloadFile: async (itemId: number, fileId: number): Promise<string> =>
    financialEntryApi.getFileUrl(itemId, fileId, true),

  deleteFile: async (itemId: number, fileId: number): Promise<void> => {
    await axios.delete(`${API_BASE}/item/${itemId}/files/${fileId}`);
  },
};

export const financialRecurrenceApi = {
  create: async (
    payload: CreateFinancialRecurrencePayload,
  ): Promise<FinancialRecurrence> => {
    const { data } = await axios.post<FinancialRecurrence>(
      RECURRENCE_API_BASE,
      payload,
    );
    return data;
  },

  list: async (): Promise<FinancialRecurrence[]> => {
    const { data } = await axios.get<FinancialRecurrence[]>(RECURRENCE_API_BASE);
    return data;
  },

  getById: async (id: number): Promise<FinancialRecurrence> => {
    const { data } = await axios.get<FinancialRecurrence>(
      `${RECURRENCE_API_BASE}/${id}`,
    );
    return data;
  },

  /** "Esta e as próximas": aplica alterações a partir de effectiveFrom. */
  updateFrom: async (
    id: number,
    payload: UpdateFinancialRecurrencePayload,
  ): Promise<FinancialRecurrence> => {
    const { data } = await axios.patch<FinancialRecurrence>(
      `${RECURRENCE_API_BASE}/${id}`,
      payload,
    );
    return data;
  },

  /** "Excluir esta e as próximas": encerra a recorrência na véspera de `from`. */
  endFrom: async (id: number, from?: string): Promise<{ deleted: boolean }> => {
    const { data } = await axios.delete<{ deleted: boolean }>(
      `${RECURRENCE_API_BASE}/${id}`,
      { params: from ? { from } : undefined },
    );
    return data;
  },

  /** Cria o lançamento real de uma ocorrência (idempotente). */
  materialize: async (
    id: number,
    payload: MaterializeOccurrencePayload,
  ): Promise<FinancialEntryDetail> => {
    const { data } = await axios.post<FinancialEntryDetail>(
      `${RECURRENCE_API_BASE}/${id}/materialize`,
      payload,
    );
    return data;
  },
};

export default financialEntryApi;
